import type {
  CashflowTargetGranularity,
  MaturityOption,
} from '@moment/horizon-data-access/proposal'
import type {
  BondStatus,
  BondType,
  TreasurySubtype,
} from '@moment/trading-types/reference-data'
import type {
  CashflowFrequency,
  CashflowSource,
} from '@/portfolio/proposals/cashflow-target/types'
import type { Statistics } from '@/types/statistics'
import type { ComplianceCheck } from './compliance'
import type { ModelConstraint } from './constraints'

// GET /accounts
// https://docs.withmoment.com/reference/get_v1-account
interface Account {
  account_id: string
  // Face value (FI) or shares (equity); LCM'd with each instrument's min_increment. Null = no constraint.
  minimum_order_increment: number | null
  account_name: string
  custodian_account_id: string
  short_positions_enabled: boolean
  moment_organization_id: string
  moment_trading_group_id: string
  custodian: Custodian
  advisor: Advisor
  household: Household
  is_taxable: boolean
  account_type: string // TODO: enum?
  management_type: ManagementType | null
  prime_brokerage_enabled: boolean
  custodial_status: string
  investor: Investor
  extra: Record<string, unknown>
  last_updated_at: string
  statistics: AccountStatistics
  compliance: AccountCompliance | null
  cash_allocation: CashWeightAllocation | CashMarketValueAllocation | null
  sleeves: Record<string, SleeveWithComplianceAndStatistics>
  moment_status: string
  desired_status: string
  status: string
  do_not_trade: boolean
  do_not_trade_reason: string
  do_not_buy?: boolean
  do_not_sell?: boolean
  margin_eligible?: boolean
  /** Effective platform settings returned on GET /account/:id/ when present. */
  settings?: AccountSettingOutput[]
  /** Denormalized minimum cash from GET /account/:id/. */
  min_cash?: AccountMinCash | null
  /** Legacy minimum cash balance in USD from the account API. */
  minimum_cash_balance?: number
  /**
   * Managed sleeve targets + buffer cash vs 100%. Present when accounts
   * returns it (allocation-sum gate); decimals are 0..1 strings.
   */
  allocation_sum?: AccountAllocationSumPayload
}

/** Public-api `allocation_sum` on an account read. Fractions are decimal strings. */
type AccountAllocationSumPayload =
  | {
      verdict: 'balanced'
      total: string
      template_owned: boolean
      managed_target_sum?: string
      notional_total?: string
      notional_sleeve_ids?: string[]
      included_sleeve_ids?: string[]
      buffer_cash_target_percent?: string
      violation?: boolean
    }
  | {
      verdict: 'under_allocated'
      total: string
      remaining: string
      template_owned: boolean
      managed_target_sum?: string
      notional_total?: string
      notional_sleeve_ids?: string[]
      included_sleeve_ids?: string[]
      buffer_cash_target_percent?: string
      violation?: boolean
    }
  | {
      verdict: 'over_allocated'
      total: string
      excess: string
      template_owned: boolean
      managed_target_sum?: string
      notional_total?: string
      notional_sleeve_ids?: string[]
      included_sleeve_ids?: string[]
      buffer_cash_target_percent?: string
      violation?: boolean
    }

export interface CashWeightAllocation {
  min_allocation_weight: number
  target_allocation_weight: number
  max_allocation_weight: number
}

export interface CashMarketValueAllocation {
  min_allocation_market_value: number
  target_allocation_market_value: number
  max_allocation_market_value: number
}

// GET /account/:account_id/
// https://docs.withmoment.com/reference/get_v1-account-account-id
export interface AccountDetail extends Account {
  lots: Lot[]
  cash: Cash[]
}

export interface AccountStatistics extends Statistics {
  cash_market_value: number
  other_market_value: number
  tracked_fixed_income_market_value: number
}

/** Marginal tax components in percentage points (e.g. 15 for 15%), per account API. */
interface TaxRates {
  federal_tax_rate: number
  state_tax_rate: number
  local_tax_rate: number | null
}

export const TAX_PROFILE_US_TAXABLE = 'us_taxable' as const
const TAX_PROFILE_CUSTOM_TAXABLE = 'custom_taxable' as const
const TAX_PROFILE_UK_TAXABLE = 'uk_taxable' as const
const TAX_PROFILE_NON_TAXABLE = 'non_taxable' as const

export type TaxProfileValue =
  | typeof TAX_PROFILE_US_TAXABLE
  | typeof TAX_PROFILE_CUSTOM_TAXABLE
  | typeof TAX_PROFILE_UK_TAXABLE
  | typeof TAX_PROFILE_NON_TAXABLE

export interface Investor {
  id: string
  name: string
  address: string
  marginal_long_term_taxes: TaxRates
  marginal_ordinary_taxes: TaxRates
  marginal_short_term_taxes?: TaxRates
  tax_state?: string | null
  tax_profile?: TaxProfileValue
  custom_income_tax?: number
  custom_capital_gains_tax?: number
}

export interface AccountCompliance {
  compliant: boolean
  checks: ComplianceCheck[]
}

interface SleeveStatistics extends Statistics {}

export interface SleeveWithComplianceAndStatistics extends Sleeve {
  statistics: SleeveStatistics
  compliance: AccountCompliance | null
  model_assignment?: ModelAssignment
}

export enum ManagementType {
  Sma = 'sma',
  Uma = 'uma',
  Unspecified = '"MANAGEMENT_TYPE_UNSPECIFIED"',
}

interface Household {
  id: string
  name: string
}

interface Custodian {
  id: string
  name: string
}

interface Advisor {
  id: string
  name: string
}

export enum AccountStatus {
  Running = 'running',
  Paused = 'paused',
}

export interface Instrument {
  isin: string
  cusip: string
  price: number
  yield_to_worst: number
  yield_to_maturity: number
  description: string
  sp_rating: string
  moodys_rating: string
  par_value: number
  status: BondStatus
  type: BondType
  treasury_subtype: TreasurySubtype | null
  maturity_date: string
  coupon: number
  duration: number
  close_price: number
  close_yield_to_maturity: number
  close_yield_to_worst: number
  issuer: string
  sector: string
  callable: boolean
  next_call_date: string
  next_call_price: number
  municipal_taxable_federal: number | null
  municipal_state: string | null
  purpose: string
  use_of_proceeds: string | null
  accrued_interest: number
  principal_factor: number
}

export interface Lot {
  moment_lot_id: string
  custodian_lot_id: string
  sleeve_id: string
  basis_date: string
  basis_price: number
  basis_quantity: number
  is_short_position: boolean
  open_quantity: number
  settled_closed_quantity: number
  settled_open_quantity: number
  unsettled_closed_quantity: number
  unsettled_open_quantity: number
  moment_reserved_quantity: number
  long_term: boolean
  wash_sale: boolean
  instrument: Instrument
  accrued_interest: number
  book_ytm?: number | null
  book_ytc?: number | null
  book_ytw?: number | null
}

export enum AllocationStrategy {
  FifoWithoutAveraging = 'fifo_without_averaging',
  FifoWithAveraging = 'fifo_with_averaging',
  ProRataWithAveraging = 'pro_rata_with_averaging',
}

export function getAllocationStrategy(
  useFifoWithAveraging: boolean,
): AllocationStrategy {
  return useFifoWithAveraging
    ? AllocationStrategy.FifoWithAveraging
    : AllocationStrategy.FifoWithoutAveraging
}

// ============================================================================
// PLANNED FOR DEPRECATION: Accounts API Order Entry Types
// ============================================================================
// These types map to the Accounts API order entry endpoints, which we are
// hoping to deprecate soon and transition everything to use the OMS order endpoints.
// ============================================================================

export interface AccountsPlaceBlockOrderResponse {
  id: string
  client_order_id: string
}

// ============================================================================
// END PLANNED FOR DEPRECATION: Accounts API Order Entry Types
// ============================================================================

export interface AccountUpdateRequest {
  investor_name?: string
  investor_address?: string
  account_name?: string
  marginal_long_term_taxes?: TaxRates
  marginal_ordinary_taxes?: TaxRates
  extra?: Record<string, unknown>
}

/** Key for US tax state abbreviation on `PUT account/{id}/settings/` (see Account Management API). */
export const ACCOUNT_SETTING_KEY_TAX_STATE = 'tax_state' as const

/** Key for the advisor-set account-name override on `PUT account/{id}/settings/`. */
export const ACCOUNT_SETTING_KEY_ACCOUNT_NAME = 'account_name' as const

/** Active minimum-cash mode on `PUT account/{id}/settings/` (`dollar` or `percent`). */
export const ACCOUNT_SETTING_KEY_MIN_CASH_SETTING = 'min_cash_setting' as const
/** Minimum cash USD amount when `min_cash_setting` is `dollar`. */
export const ACCOUNT_SETTING_KEY_MIN_CASH_DOLLAR = 'min_cash_dollar' as const
/** Minimum cash as a decimal fraction in (0, 1] (e.g. `0.4` for 40%) when `min_cash_setting` is `percent`. */
export const ACCOUNT_SETTING_KEY_MIN_CASH_PERCENT = 'min_cash_percent' as const

/**
 * Buffer cash bounds on `PUT account/{id}/settings/`, each a decimal fraction in
 * [0, 1] (e.g. `0.05` for 5%) of managed value. Independent of one another, but
 * accounts rejects a write where min exceeds target or target exceeds max.
 */
export const ACCOUNT_SETTING_KEY_BUFFER_CASH_MIN_PERCENT =
  'buffer_cash_min_percent' as const
export const ACCOUNT_SETTING_KEY_BUFFER_CASH_TARGET_PERCENT =
  'buffer_cash_target_percent' as const
export const ACCOUNT_SETTING_KEY_BUFFER_CASH_MAX_PERCENT =
  'buffer_cash_max_percent' as const

export const MIN_CASH_SETTING_VALUE_DOLLAR = 'dollar' as const
export const MIN_CASH_SETTING_VALUE_PERCENT = 'percent' as const

/** Minimum cash object returned on GET /account/:id/. */
export interface AccountMinCash {
  min_cash_setting?: string
  min_cash_dollar?: string
  min_cash_percent?: string
}

/** Marginal rate in percentage points (e.g. `15` for 15%), one setting per component. */
export const ACCOUNT_SETTING_KEY_TAX_RATE_SHORT_TERM_FEDERAL =
  'tax_rate_short_term_federal' as const
export const ACCOUNT_SETTING_KEY_TAX_RATE_SHORT_TERM_STATE =
  'tax_rate_short_term_state' as const
export const ACCOUNT_SETTING_KEY_TAX_RATE_SHORT_TERM_LOCAL =
  'tax_rate_short_term_local' as const
export const ACCOUNT_SETTING_KEY_TAX_RATE_LONG_TERM_FEDERAL =
  'tax_rate_long_term_federal' as const
export const ACCOUNT_SETTING_KEY_TAX_RATE_LONG_TERM_STATE =
  'tax_rate_long_term_state' as const
export const ACCOUNT_SETTING_KEY_TAX_RATE_LONG_TERM_LOCAL =
  'tax_rate_long_term_local' as const

// Legacy API stored JSON blobs under `marginal_short_term_taxes` and
// `marginal_long_term_taxes`; new clients use the flat `tax_rate_*` keys above.

/** Per-account ST/LT realized-gain budget ceilings in dollars. */
export const ACCOUNT_SETTING_KEY_TAX_BUDGET_SHORT_TERM =
  'tax_budget_short_term' as const
export const ACCOUNT_SETTING_KEY_TAX_BUDGET_LONG_TERM =
  'tax_budget_long_term' as const
export const ACCOUNT_SETTING_KEY_TAX_BUDGET_ESTIMATED_TAX_BILL =
  'tax_budget_estimated_tax_bill' as const
/** Per-account total realized-gain budget ceiling in dollars (independent of ST/LT). */
export const ACCOUNT_SETTING_KEY_TAX_BUDGET_TOTAL =
  'tax_budget_total_gains' as const
/** Per-account tax sensitivity, an integer 0-100. */
export const ACCOUNT_SETTING_KEY_TAX_SENSITIVITY = 'tax_sensitivity' as const
/** Proration period for every tax budget above; absent means whole-year. */
export const ACCOUNT_SETTING_KEY_TAX_BUDGET_PERIOD =
  'tax_budget_period' as const
/** Per-account master switch: wash-sale prevention is enforced only when set to 'true'. */
export const ACCOUNT_SETTING_KEY_WASH_SALE_PREVENTION_ENABLED =
  'wash_sale_prevention_enabled' as const

/**
 * Advisor override keeping prime brokerage available for the rest of the New
 * York day it is written; accounts accepts only 'true'. Delete it to revert.
 */
export const ACCOUNT_SETTING_KEY_PRIME_BROKERAGE_OVERRIDE =
  'prime_brokerage_override' as const

export interface AccountSettingInput {
  setting_key: string
  setting_value: string
  effective_date?: string
}

export interface UpdateAccountSettingsRequest {
  settings: AccountSettingInput[]
  delete_keys?: string[]
}

export interface AccountSettingOutput {
  setting_key: string
  setting_value: string
  effective_date?: string
}

export interface UpdateAccountSettingsResponse {
  account_id: string
  settings: AccountSettingOutput[]
}

export interface Cash {
  moment_lot_id: string
  custodian_lot_id: string
  currency: string
  moment_reserved_quantity: number
  unsettled_open_quantity: number
  settled_open_quantity: number
  settled_closed_quantity: number
  unsettled_closed_quantity: number
  open_quantity?: number
  sleeve_id?: string
  account_weight?: number
}

export interface Sleeve {
  id: string
  name: string
  is_default: boolean
  // Managed sleeves are billed, count toward the account's managed value and
  // are optimized. Optional while older payloads are still in flight.
  is_managed?: boolean
  min_allocation: number
  target_allocation: number
  max_allocation: number
  allocation_basis?: 'weight' | 'market_value'
  extra: Record<string, unknown> | null
  compliance: AccountCompliance | null
}

export interface AccountStatisticsRequest {
  id: string
  start_date: string // YYYY-MM-DD
  end_date: string // YYYY-MM-DD
  frequency: '1day' | '15min'
}

export interface Position {
  id: string
  isin: string
  // An ISIN can have multiple lots and be in multiple sleeves
  sleeve_ids: string[]
  par_value: number
  description: string
  issuer: string
  coupon: number
  maturity_date: string
  sp_rating: string
  moodys_rating?: string
  current_price: number
  avg_cost_basis: number
  yield_to_worst: number
  total_basis_quantity: number
  total_accrued_interest: number
  weight: number
  market_value: number
  cost_basis_total: number
  gain_loss: number
  open_quantity: number
  sellable_quantity: number
  sector: string
  cusip: string
  type: BondType
  trade_status?: 'buy' | 'sell' | 'hold'
  book_ytm?: number | null
  book_ytc?: number | null
  book_ytw?: number | null
}

//
// Sleeve management
//

interface SleeveAccountParams {
  account_id: string
}

export interface SleeveIdentifierParams extends SleeveAccountParams {
  sleeve_id: string
}

export interface CreateSleeveParams extends SleeveAccountParams {
  body: CreateSleeveRequest
}

export interface CreateSleeveResponse {
  message: string
  sleeve_id: string
}

export interface UpdateSleeveParams extends SleeveIdentifierParams {
  body: UpdateSleeveRequest
}

export interface SleeveHoldingsAssignmentParams extends SleeveIdentifierParams {
  body: SleeveHoldingsAssignmentRequest
}

export interface SleeveModelAssignmentParams extends SleeveIdentifierParams {
  body: ModelAssignment
}

interface CreateSleeveRequest {
  name: string
  min_allocation: number
  target_allocation: number
  max_allocation: number
  allocation_basis?: 'weight' | 'market_value'
  model_assignment?: ModelAssignment
  // Omitted means managed: accounts applies that default, and sending false
  // alongside a model_assignment is rejected.
  is_managed?: boolean
  extra: Record<string, unknown>
}

interface UpdateSleeveRequest {
  name?: string
  min_allocation?: number
  target_allocation?: number
  max_allocation?: number
  allocation_basis?: 'weight' | 'market_value'
  // Omitting it leaves the flag alone; the backend refuses to unmanage a
  // sleeve that holds a strategy, or the funding sleeve.
  is_managed?: boolean
  extra?: Record<string, unknown>
}

interface SleeveHoldingsAssignmentRequest {
  lots: string[] // Array of lot IDs
}

export type SleeveHoldingsAssignmentResponse = Record<string, unknown>

/**
 * One transaction to move, for the direct reassign. `source_sleeve_id` is the
 * sleeve the caller believes the transaction currently sits in; the backend
 * rejects the item if it no longer matches, which is what makes a duplicate or
 * concurrent submit a no-op instead of moving the cash twice.
 */
export interface TransactionReassignmentItem {
  account_id: string
  transaction_id: string
  source_sleeve_id: string
  target_sleeve_id: string
}

export interface ReassignTransactionsParams {
  items: TransactionReassignmentItem[]
}

export interface BatchReassignTransactionsParams {
  // Transactions to route; may span accounts. The backend resolves each
  // transaction's owning account itself, so no account is sent.
  transaction_ids: string[]
  // Candidate strategies. Per account the backend routes to the sleeve for the
  // one strategy that account holds; accounts holding none, or more than one,
  // are skipped. The accounts endpoint names this wire field `strategy_ids`.
  strategy_ids: string[]
}

interface TransactionReassignmentResult {
  account_id: string
  transaction_id: string
  /** Set on success only. */
  routing_id?: string
  /** Set on skip/failure only. */
  reason?: string
}

/**
 * Per-item outcome partition — a 2xx can still move nothing. `skipped` is
 * client-actionable (already in the target sleeve, no cash effect, stale source
 * sleeve, no/ambiguous strategy sleeve); `failed` is an infra error, already
 * logged server-side behind a fixed reason.
 */
export interface TransactionReassignmentResponse {
  processed: TransactionReassignmentResult[]
  skipped: TransactionReassignmentResult[]
  failed: TransactionReassignmentResult[]
}

export type LotOverrideField =
  | 'acquired_date'
  | 'settlement_date'
  | 'trade_date'
  | 'unadj_cost_per_unit'

interface LotOverrideInput {
  field: LotOverrideField
  value: string
  reason?: string
}

interface LotOverrideRevertTarget {
  field: LotOverrideField
}

/** PUT /v1/lot/{lot_id}/overrides/ */
interface UpsertLotOverridesRequestBody {
  overrides?: LotOverrideInput[]
  revert_targets?: LotOverrideRevertTarget[]
}

export interface UpdateLotParams {
  lot_id: string
  body: UpsertLotOverridesRequestBody
}

/** Sectioned container for a model assignment's constraints. */
interface CustomizationsContainer {
  fixed_income?: Record<string, ModelConstraint | null>
}

export interface ModelAssignment {
  model_id: string
  model_name?: string
  assignment_date: string
  customizations: CustomizationsContainer
  /** The sleeve's own model cash, a fraction in [0, 1). Omitted means the model's cash_buffer; a PUT without it clears it. */
  cash_buffer_override?: number
  rebalance_strategy: string
  meta?: Record<string, unknown>
  cashflow_targets?: CashflowTarget[]
  cashflow_target_priority?: number
}

export type SleeveModelAssignmentResponse = Record<string, unknown>

export interface CashflowTarget {
  amount?: number
  start_date: string | null
  end_date: string
  frequency: CashflowFrequency
  granularity?: CashflowTargetGranularity
  source: CashflowSource
  overrides: Record<string, number>
  call_assumption: MaturityOption
}

// PUT /v1/account/{account_id}/restrictions/
// https://docs.withmoment.com/reference/updateaccountrestrictions
export type AccountRestrictionType =
  | 'DO_NOT_TRADE'
  | 'DO_NOT_BUY'
  | 'DO_NOT_SELL'

interface AccountRestrictionInput {
  restriction_type: AccountRestrictionType
  reason?: string
}

export interface UpdateAccountRestrictionsRequestBody {
  restrictions: AccountRestrictionInput[]
  clear_restriction_types?: AccountRestrictionType[]
}

interface AccountRestrictionOutput {
  restriction_type: AccountRestrictionType
  reason: string
}

export interface UpdateAccountRestrictionsResponse {
  entity_type: string
  entity_id: string
  restrictions: AccountRestrictionOutput[]
}

// POST /v1/accounts/restrictions/bulk/
// Applies the same restriction change to many accounts (all-or-nothing).
export interface BulkUpdateAccountRestrictionsRequestBody
  extends UpdateAccountRestrictionsRequestBody {
  account_ids: string[]
}

interface BulkAccountRestrictionResult {
  account_id: string
  restrictions: AccountRestrictionOutput[]
}

export interface BulkUpdateAccountRestrictionsResponse {
  results: BulkAccountRestrictionResult[]
}

// PUT /v1/account/{account_id}/wash-sale-overrides/
// Disables or re-enables a single wash-sale restriction for one instrument.
export type WashSaleRestrictionType = 'BUY_BLOCK' | 'SELL_BLOCK'

export interface SetWashSaleOverrideRequestBody {
  instrument_id: string
  restriction_type: WashSaleRestrictionType
  // true disables enforcement for the instrument (creates the override); false
  // re-enables it (removes the override).
  disabled: boolean
}

export interface SetWashSaleOverrideResponse {
  account_id: string
  instrument_id: string
  restriction_type: WashSaleRestrictionType
  disabled: boolean
}
